import { createContext, useContext, useReducer, ReactNode } from 'react';
import { PhotoData } from '@/types';

interface PhotoState {
  photos: PhotoData[];
  selectedPhoto: PhotoData | null;
  loading: boolean;
  error: string | null;
}

type PhotoAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PHOTOS'; payload: PhotoData[] }
  | { type: 'ADD_PHOTO'; payload: PhotoData }
  | { type: 'UPDATE_PHOTO'; payload: PhotoData }
  | { type: 'DELETE_PHOTO'; payload: string }
  | { type: 'SELECT_PHOTO'; payload: PhotoData | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

const photoReducer = (state: PhotoState, action: PhotoAction): PhotoState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_PHOTOS':
      return { ...state, photos: action.payload, loading: false, error: null };
    case 'ADD_PHOTO':
      return { ...state, photos: [...state.photos, action.payload] };
    case 'UPDATE_PHOTO':
      return {
        ...state,
        photos: state.photos.map(p => p.id === action.payload.id ? action.payload : p),
        selectedPhoto: state.selectedPhoto?.id === action.payload.id ? action.payload : state.selectedPhoto
      };
    case 'DELETE_PHOTO':
      return {
        ...state,
        photos: state.photos.filter(p => p.id !== action.payload),
        selectedPhoto: state.selectedPhoto?.id === action.payload ? null : state.selectedPhoto
      };
    case 'SELECT_PHOTO':
      return { ...state, selectedPhoto: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

const PhotoStateContext = createContext<{
  state: PhotoState;
  dispatch: React.Dispatch<PhotoAction>;
} | undefined>(undefined);

export function PhotoStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(photoReducer, {
    photos: [],
    selectedPhoto: null,
    loading: false,
    error: null,
  });

  return (
    <PhotoStateContext.Provider value={{ state, dispatch }}>
      {children}
    </PhotoStateContext.Provider>
  );
}

export function usePhotoState() {
  const context = useContext(PhotoStateContext);
  if (!context) {
    throw new Error('usePhotoState must be used within PhotoStateProvider');
  }
  return context;
}